import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Upload, Loader2, Info, Activity, TrendingUp, Ruler,
  Scale, User, Calendar, ChevronRight, CheckCircle, AlertTriangle, ImageIcon, Download
} from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import GrowthCurveChart from "@/components/GrowthCurveChart";
import PaywallModal from "@/components/PaywallModal";
import DOBPicker from "@/components/DOBPicker";
import { ageFromDOB, toISODate, formatAgeDisplay } from "@/utils/age";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BoneAHPage = () => {
  useTheme();
  const [activeTab, setActiveTab] = useState("bone-age");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [boneResult, setBoneResult] = useState(null);
  const [heightResult, setHeightResult] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  const [boneForm, setBoneForm] = useState({ name: "", sex: "" });
  const [boneDob, setBoneDob] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [heightDob, setHeightDob] = useState(null);
  const [heightForm, setHeightForm] = useState({
    name: "", current_height: "", present_weight: "",
    bone_age: "", gender: "", ethnicity: "ind", father_height: "", mother_height: ""
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await api.post(`${API}/boneah/generate-report`, {
        bone_data: boneResult || null,
        height_data: heightResult || null
      }, { responseType: 'blob', timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const name = boneResult?.name || heightResult?.name || 'Kid';
      link.download = `BoneAH_Report_${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (error) {
      // 402 → show paywall
      if (error.response?.status === 402) {
        try {
          const me = await api.get(`${API}/payments/me`);
          setCreditBalance(me.data.balance || 0);
        } catch {}
        setPaywallOpen(true);
        return;
      }
      let msg = "Failed to generate report";
      if (error.code === "ERR_NETWORK") msg = "Network error — please check your internet connection.";
      else if (error.response?.data) {
        try { const txt = await error.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch {}
      }
      toast.error(msg);
      console.error("PDF error:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX = 512;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(ev.target.result);
        img.src = ev.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const handleBoneSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error("Please upload an X-ray image"); return; }
    if (!boneForm.name || !boneDob || boneForm.sex === "") { toast.error("Please fill all fields"); return; }
    const ageInfo = ageFromDOB(boneDob);
    if (!ageInfo || ageInfo.decimalYears <= 0 || ageInfo.decimalYears > 18) {
      toast.error("Date of birth must give an age between 0 and 18 years"); return;
    }

    setLoading(true);
    setBoneResult(null);
    try {
      // Convert image to base64 (compressed to 512x512) and send as JSON
      // This avoids Cloudflare blocking multipart file uploads
      const imageBase64 = await compressImage(selectedFile);
      if (!imageBase64) { throw new Error("Failed to process image"); }

      const response = await api.post(`${API}/boneah/predict-bone-age-b64`, {
        image_base64: imageBase64,
        name: boneForm.name,
        age: ageInfo.decimalYears,
        sex: parseInt(boneForm.sex)
      }, { timeout: 120000 });
      setBoneResult(response.data);
      toast.success("Bone age prediction complete!");
    } catch (error) {
      let msg = "Prediction failed. Please try again.";
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network")) {
        msg = "Network error — please check your internet connection and try again. If the issue persists, try with a smaller image.";
      } else if (error.code === "ECONNABORTED") {
        msg = "Request timed out — your image may be too large. Try a smaller file.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
      console.error("Bone age error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHeightSubmit = async (e) => {
    e.preventDefault();
    if (!heightDob) { toast.error("Please pick the child's date of birth"); return; }
    const ageInfo = ageFromDOB(heightDob);
    if (!ageInfo || ageInfo.decimalYears <= 0 || ageInfo.decimalYears > 18) {
      toast.error("Date of birth must give an age between 0 and 18 years"); return;
    }
    const required = ['name', 'current_height', 'present_weight', 'bone_age', 'gender'];
    for (const f of required) {
      if (!heightForm[f]) { toast.error(`Please fill in ${f.replace(/_/g, ' ')}`); return; }
    }
    setLoading(true);
    setHeightResult(null);
    try {
      const payload = {
        ...heightForm,
        chronological_age: ageInfo.decimalYears,
        date_of_birth: toISODate(heightDob),
        current_height: parseFloat(heightForm.current_height),
        present_weight: parseFloat(heightForm.present_weight),
        bone_age: parseFloat(heightForm.bone_age),
        father_height: heightForm.father_height ? parseFloat(heightForm.father_height) : null,
        mother_height: heightForm.mother_height ? parseFloat(heightForm.mother_height) : null,
        // True only when bone_age came from the X-ray AI prediction on this page.
        xray_provided: Boolean(boneResult && boneResult.bone_age_years),
      };
      const response = await api.post(`${API}/boneah/predict-height-weight`, payload, { timeout: 30000 });
      setHeightResult(response.data);
      toast.success("Height & weight prediction complete!");
    } catch (error) {
      let msg = "Prediction failed. Please try again.";
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network")) {
        msg = "Network error — please check your internet connection and try again.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
      console.error("Height/Weight error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getGHIColor = (ghi) => {
    if (ghi >= 80) return "#4ECDC4";
    if (ghi >= 60) return "#FFE66D";
    if (ghi >= 40) return "#FF6B6B";
    return "#FF6B6B";
  };

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#4ECDC4] transition-colors mb-6 text-sm" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Back to tots360
            </Link>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3" data-testid="boneah-page-title">
              Bone <span className="text-[#4ECDC4]">AH</span>
            </h1>
            <p className="text-white/50 text-lg">AI-Powered Bone Age Assessment & Growth Prediction</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white/[0.03] p-1 rounded-xl border border-white/10 w-full grid grid-cols-2">
              <TabsTrigger value="bone-age" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-[#0a1628] text-white/50" data-testid="tab-bone-age">
                <ImageIcon className="w-4 h-4 mr-2" /> Bone Age Prediction
              </TabsTrigger>
              <TabsTrigger value="height-weight" className="rounded-lg data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-[#0a1628] text-white/50" data-testid="tab-height-weight">
                <Ruler className="w-4 h-4 mr-2" /> Height & Weight Assessment
              </TabsTrigger>
            </TabsList>

            {/* Bone Age Tab */}
            <TabsContent value="bone-age" className="space-y-6">
              <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-xl text-white font-display">Upload Hand X-Ray</CardTitle>
                  <CardDescription className="text-white/40">Upload a left-hand X-ray for AI bone age determination</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleBoneSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70"><User className="w-4 h-4 text-[#4ECDC4]" /> Kid Name</Label>
                        <Input placeholder="Enter name" value={boneForm.name}
                          onChange={(e) => setBoneForm(p => ({ ...p, name: e.target.value }))}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="bone-input-name" />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70"><Calendar className="w-4 h-4 text-[#4ECDC4]" /> Date of Birth</Label>
                        <DOBPicker value={boneDob} onChange={setBoneDob} testId="boneah-bone-dob" />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70"><User className="w-4 h-4 text-[#4ECDC4]" /> Sex</Label>
                        <Select value={boneForm.sex} onValueChange={(v) => setBoneForm(p => ({ ...p, sex: v }))}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="bone-select-sex">
                            <SelectValue placeholder="Select sex" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a2e] border-white/10">
                            <SelectItem value="0" className="text-white">Male</SelectItem>
                            <SelectItem value="1" className="text-white">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Upload Area */}
                    <div
                      className="relative border-2 border-dashed border-white/20 rounded-2xl p-8 text-center cursor-pointer hover:border-[#4ECDC4]/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()} data-testid="xray-upload-area"
                    >
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                      {imagePreview ? (
                        <div className="space-y-3">
                          <img src={imagePreview} alt="X-ray preview" className="max-h-64 mx-auto rounded-xl object-contain" />
                          <p className="text-sm text-[#4ECDC4]">{selectedFile?.name} | Click to change</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="w-12 h-12 text-white/20 mx-auto" />
                          <p className="text-white/40">Click to upload left-hand X-ray image</p>
                          <p className="text-xs text-white/20">Supports JPG, PNG, DICOM</p>
                        </div>
                      )}
                    </div>

                    <Button type="submit" className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] text-lg py-6 rounded-xl font-semibold"
                      disabled={loading} data-testid="bone-submit-btn">
                      {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing X-Ray...</>) : "Predict Bone Age"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Bone Age Result */}
              <AnimatePresence>
                {boneResult && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="bone-result-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white font-display">
                          <Activity className="w-5 h-5 text-[#4ECDC4]" /> Bone Age Results for {boneResult.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-4 bg-[#4ECDC4]/10 rounded-xl border border-[#4ECDC4]/30 text-center">
                            <p className="text-xs text-[#4ECDC4] uppercase tracking-wide mb-1">Predicted Bone Age</p>
                            <p className="text-3xl font-bold text-white" data-testid="bone-age-result">{boneResult.bone_age_formatted}</p>
                            <p className="text-xs text-white/30 mt-1">({boneResult.bone_age_years} years)</p>
                          </div>
                          <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10 text-center">
                            <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Chronological Age</p>
                            <p className="text-3xl font-bold text-white">{boneResult.known_age} years</p>
                          </div>
                          <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10 text-center">
                            <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Difference</p>
                            <p className="text-3xl font-bold text-white">{boneResult.age_difference} years</p>
                          </div>
                        </div>

                        <div className={`p-4 rounded-xl border flex items-start gap-3 ${boneResult.is_consistent
                          ? "bg-[#4ECDC4]/10 border-[#4ECDC4]/30"
                          : "bg-[#FF6B6B]/10 border-[#FF6B6B]/30"}`}>
                          {boneResult.is_consistent
                            ? <CheckCircle className="w-5 h-5 text-[#4ECDC4] mt-0.5 shrink-0" />
                            : <AlertTriangle className="w-5 h-5 text-[#FF6B6B] mt-0.5 shrink-0" />}
                          <div>
                            <p className={`font-semibold ${boneResult.is_consistent ? "text-[#4ECDC4]" : "text-[#FF6B6B]"}`}>
                              {boneResult.is_consistent ? "Consistent" : "Attention Required"}
                            </p>
                            <p className="text-white/60 text-sm" data-testid="bone-conclusion">{boneResult.conclusion}</p>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button onClick={() => { setActiveTab("height-weight"); setHeightForm(p => ({ ...p, name: boneResult.name, bone_age: String(boneResult.bone_age_years) })); }}
                            className="bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold" data-testid="proceed-to-height-btn">
                            Proceed to Height/Weight Assessment <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                          <Button onClick={downloadPDF} disabled={pdfLoading}
                            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold" data-testid="bone-download-pdf-btn">
                            {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            Download PDF Report
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            {/* Height/Weight Tab */}
            <TabsContent value="height-weight" className="space-y-6">
              <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-xl text-white font-display">Growth Assessment</CardTitle>
                  <CardDescription className="text-white/40">Predict adult height and assess growth using bone age data</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleHeightSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70">
                          <Calendar className="w-4 h-4 text-[#4ECDC4]" /> Date of Birth
                        </Label>
                        <DOBPicker value={heightDob} onChange={setHeightDob} testId="boneah-height-dob" />
                      </div>
                      {[
                        { key: "name", label: "Kid Name", icon: User, placeholder: "Enter name", type: "text" },
                        { key: "gender", label: "Gender", icon: User, type: "select", options: [{ v: "male", l: "Male" }, { v: "female", l: "Female" }] },
                        { key: "bone_age", label: "Bone Age (years)", icon: Calendar, placeholder: "e.g., 7.5", type: "number", step: "0.5" },
                        { key: "current_height", label: "Current Height (cm)", icon: Ruler, placeholder: "e.g., 106", type: "number" },
                        { key: "present_weight", label: "Present Weight (kg)", icon: Scale, placeholder: "e.g., 19", type: "number" },
                        { key: "father_height", label: "Father's Height (cm, optional)", icon: User, placeholder: "e.g., 176", type: "number" },
                        { key: "mother_height", label: "Mother's Height (cm, optional)", icon: User, placeholder: "e.g., 150", type: "number" },
                      ].map((field) => (
                        <div key={field.key} className="space-y-2">
                          <Label className="flex items-center gap-2 text-white/70">
                            <field.icon className="w-4 h-4 text-[#4ECDC4]" /> {field.label}
                          </Label>
                          {field.type === "select" ? (
                            <Select value={heightForm[field.key]} onValueChange={(v) => setHeightForm(p => ({ ...p, [field.key]: v }))}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`height-select-${field.key}`}>
                                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1a1a2e] border-white/10">
                                {field.options.map(o => (<SelectItem key={o.v} value={o.v} className="text-white">{o.l}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input type={field.type} step={field.step} placeholder={field.placeholder}
                              value={heightForm[field.key]} onChange={(e) => setHeightForm(p => ({ ...p, [field.key]: e.target.value }))}
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                              data-testid={`height-input-${field.key}`} />
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Info className="w-4 h-4 text-[#4ECDC4]" /> Ethnicity</Label>
                      <Select value={heightForm.ethnicity} onValueChange={(v) => setHeightForm(p => ({ ...p, ethnicity: v }))}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="height-select-ethnicity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          {[
                            { v: "ind", l: "Indian" }, { v: "caueurn", l: "Caucasian (N. European)" },
                            { v: "caueurc", l: "Caucasian (C. European)" }, { v: "asijap", l: "Asian (Japanese)" },
                            { v: "asichi", l: "Asian (Chinese)" }, { v: "afram", l: "African American" },
                          ].map(o => (<SelectItem key={o.v} value={o.v} className="text-white">{o.l}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button type="submit" className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] text-lg py-6 rounded-xl font-semibold"
                      disabled={loading} data-testid="height-submit-btn">
                      {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>) : "Predict Height & Weight"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Height/Weight Result */}
              <AnimatePresence>
                {heightResult && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="height-result-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white font-display">
                          <TrendingUp className="w-5 h-5 text-[#4ECDC4]" /> Growth Report for {heightResult.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {[
                            { label: "Predicted Final Height", value: `${heightResult.ahp_xplusp} cm`, color: "#4ECDC4" },
                            { label: "Growth Remaining", value: `${heightResult.growth_remaining_years} yrs`, color: "#FFE66D" },
                            { label: "APHV", value: `${heightResult.aphv} yrs`, color: "#A855F7" },
                            { label: "GHI Score", value: `${heightResult.growth_health_index}/100`, color: getGHIColor(heightResult.growth_health_index) },
                          ].map((m, i) => (
                            <div key={i} className="p-4 rounded-xl border text-center" style={{ background: `${m.color}10`, borderColor: `${m.color}30` }}>
                              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: m.color }}>{m.label}</p>
                              <p className="text-2xl font-bold text-white">{m.value}</p>
                            </div>
                          ))}
                        </div>

                        {/* Interactive Growth Curves */}
                        <GrowthCurveChart heightResult={heightResult} boneResult={boneResult} />

                        {/* Detailed Report */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <h3 className="text-white font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-[#4ECDC4]" /> Bone Age Assessment</h3>
                            {[
                              { label: "Chronological Age", value: heightDob ? formatAgeDisplay(heightDob) : `${heightResult.chronological_age} years` },
                              { label: "Bone Age", value: `${heightResult.bone_age} years` },
                              { label: "Difference", value: `${heightResult.bone_age_difference > 0 ? "+" : ""}${heightResult.bone_age_difference} years` },
                              { label: "Maturity Status", value: heightResult.maturity_status },
                              { label: "Carpal Bone Maturity", value: heightResult.carpal_bone_maturity },
                            ].map((item, i) => (
                              <div key={i} className="flex justify-between items-center p-2 bg-white/[0.02] rounded-lg">
                                <span className="text-sm text-white/40">{item.label}</span>
                                <span className="text-sm font-medium text-white">{item.value}</span>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-3">
                            <h3 className="text-white font-semibold flex items-center gap-2"><Ruler className="w-4 h-4 text-[#4ECDC4]" /> Height Predictions</h3>
                            {[
                              { label: "AHP (X-ray)", value: `${heightResult.ahp_xray} cm` },
                              ...(heightResult.ahp_parental ? [{ label: "AHP (Parental)", value: `${heightResult.ahp_parental} cm` }] : []),
                              { label: "Present Height Given", value: `${heightResult.present_height} cm` },
                              { label: "Predicted Final Height (Combined)", value: `${heightResult.ahp_xplusp} cm` },
                              { label: "Present Weight", value: `${heightResult.present_weight} kg` },
                              { label: "Predicted Weight", value: `${heightResult.predicted_weight} kg` },
                              { label: "Weight Status", value: heightResult.weight_status },
                            ].map((item, i) => (
                              <div key={i} className="flex justify-between items-center p-2 bg-white/[0.02] rounded-lg">
                                <span className="text-sm text-white/40">{item.label}</span>
                                <span className="text-sm font-medium text-white">{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* GHI Bar */}
                        <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-white">Growth Health Index (GHI)</span>
                            <span className="text-sm font-bold" style={{ color: getGHIColor(heightResult.growth_health_index) }}>
                              {heightResult.growth_health_index >= 80 ? "Excellent" : heightResult.growth_health_index >= 60 ? "Good" : heightResult.growth_health_index >= 40 ? "Fair" : "Poor"}
                            </span>
                          </div>
                          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${heightResult.growth_health_index}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                              className="h-full rounded-full" style={{ backgroundColor: getGHIColor(heightResult.growth_health_index) }} />
                          </div>
                          <p className="text-xs text-white/30 mt-1">{heightResult.growth_health_index}/100</p>
                        </div>

                        {/* Download PDF */}
                        <div className="pt-4">
                          <Button onClick={downloadPDF} disabled={pdfLoading}
                            className="w-full bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold py-5 text-base" data-testid="height-download-pdf-btn">
                            {pdfLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
                            Download Complete Report (PDF)
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
      <Footer />
      <Toaster position="top-right" />
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} balance={creditBalance} />
    </div>
  );
};

export default BoneAHPage;
